import React, { useState } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import clientAuthService from '../../services/clientAuthService';
import AuthBackgroundLayout from '../../components/AuthBackgroundLayout';

const ClientRegister = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [customerCode, setCustomerCode] = useState('');
  const [clientName, setClientName] = useState('');
  const [formData, setFormData] = useState({
    full_name: '', email: '', password: '', confirmPassword: ''
  });
  const [verificationCode, setVerificationCode] = useState('');

  const handleCheckCustomerCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await clientAuthService.checkCustomerCode(customerCode);
      setClientName(data.client_name);
      setFormData(prev => ({ ...prev, email: data.email }));
      setSuccess('Customer code found! Please complete registration.');
      setStep(2);
    } catch (err) {
      setError(err.error || 'Failed to verify customer code');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.full_name || !formData.password) {
      setError('Please fill in all fields');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await clientAuthService.register({
        full_name: formData.full_name,
        email: formData.email,
        password: formData.password,
        customer_code: customerCode,
      });
      setSuccess('Registration successful! Please check your email for the verification code.');
      setStep(3);
    } catch (err) {
      setError(err.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setError('');
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }
    setLoading(true);
    try {
      await clientAuthService.verifyEmail(customerCode, verificationCode);
      setSuccess('Email verified! Redirecting to login...');
      setTimeout(() => navigate('/client', { state: { showLogin: true } }), 2000);
    } catch (err) {
      setError(err.error || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setLoading(true);
    try {
      await clientAuthService.resendVerification(customerCode);
      setSuccess('Verification code sent! Please check your email.');
    } catch (err) {
      setError(err.error || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <AuthBackgroundLayout>
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <div className="text-end mb-2">
            <Button
              variant="light"
              size="sm"
              className="shadow-sm"
              onClick={() => navigate('/client', { replace: true })}
            >
              <i className="fas fa-home me-1"></i>Home
            </Button>
          </div>
          <Card className="shadow-lg">
            <Card.Header className="text-white text-center py-3"
              style={{ background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)' }}>
              <h3 className="mb-0">
                <i className="fas fa-user-plus me-2"></i>
                Register — Client Portal
              </h3>
              <small>Equipment Inspection Certificate Portal</small>
            </Card.Header>

            <Card.Body className="p-4">
              {/* Progress */}
              <div className="mb-4">
                <div className="d-flex justify-content-between mb-2">
                  {['Customer Code', 'Registration', 'Verification'].map((label, i) => (
                    <small key={label} className={step >= i + 1 ? 'text-primary fw-bold' : 'text-muted'}>
                      {i + 1}. {label}
                    </small>
                  ))}
                </div>
                <div className="progress" style={{ height: '5px' }}>
                  <div className="progress-bar bg-primary" style={{ width: `${(step / 3) * 100}%` }}></div>
                </div>
              </div>

              {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
              {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

              {/* Step 1 */}
              {step === 1 && (
                <Form onSubmit={handleCheckCustomerCode}>
                  <Form.Group className="mb-3">
                    <Form.Label>Customer Code <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter your customer code"
                      value={customerCode}
                      onChange={(e) => setCustomerCode(e.target.value)}
                      required
                    />
                    <Form.Text className="text-muted">
                      Enter the customer code provided to your company to verify your identity
                    </Form.Text>
                  </Form.Group>
                  <Button variant="primary" type="submit" className="w-100" disabled={loading}>
                    {loading
                      ? <><span className="spinner-border spinner-border-sm me-2"></span>Verifying...</>
                      : <><i className="fas fa-search me-2"></i>Verify Customer Code</>}
                  </Button>
                </Form>
              )}

              {/* Step 2 */}
              {step === 2 && (
                <Form onSubmit={handleRegister}>
                  <Alert variant="info">
                    <strong>Customer Found:</strong> {clientName}
                    <br />
                    <small>Email on file: {formData.email}</small>
                  </Alert>

                  <Form.Group className="mb-3">
                    <Form.Label>Your Full Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="full_name"
                      placeholder="Enter your full name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                    <Form.Control type="email" value={formData.email} disabled className="bg-light" />
                    <Form.Text className="text-muted">Email is fetched from your customer record</Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Password <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="password"
                      name="password"
                      placeholder="Create a strong password (min 6 characters)"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      minLength={6}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Confirm Password <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="password"
                      name="confirmPassword"
                      placeholder="Confirm your password"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>

                  <div className="d-grid gap-2">
                    <Button variant="primary" type="submit" disabled={loading}>
                      {loading
                        ? <><span className="spinner-border spinner-border-sm me-2"></span>Registering...</>
                        : <><i className="fas fa-user-check me-2"></i>Register</>}
                    </Button>
                    <Button variant="outline-secondary" onClick={() => setStep(1)}>
                      <i className="fas fa-arrow-left me-2"></i>Back
                    </Button>
                  </div>
                </Form>
              )}

              {/* Step 3 */}
              {step === 3 && (
                <Form onSubmit={handleVerifyEmail}>
                  <div className="text-center mb-4">
                    <i className="fas fa-envelope-open text-primary" style={{ fontSize: '3rem' }}></i>
                    <h5 className="mt-3">Verify Your Email</h5>
                    <p className="text-muted">
                      We've sent a 6-digit verification code to<br />
                      <strong>{formData.email}</strong>
                    </p>
                  </div>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-center w-100">Verification Code</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      className="text-center fs-4"
                      required
                    />
                  </Form.Group>
                  <div className="d-grid gap-2">
                    <Button variant="primary" type="submit" disabled={loading}>
                      {loading
                        ? <><span className="spinner-border spinner-border-sm me-2"></span>Verifying...</>
                        : <><i className="fas fa-check-circle me-2"></i>Verify Email</>}
                    </Button>
                    <Button variant="outline-primary" onClick={handleResendCode} disabled={loading}>
                      <i className="fas fa-redo me-2"></i>Resend Code
                    </Button>
                  </div>
                </Form>
              )}
            </Card.Body>

            <Card.Footer className="text-center py-3">
              <small>
                Already have an account?{' '}
                <a
                  href="/client"
                  className="text-primary fw-bold"
                  onClick={(e) => { e.preventDefault(); navigate('/client', { state: { showLogin: true } }); }}
                >
                  Login here
                </a>
              </small>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
    </AuthBackgroundLayout>
  );
};

export default ClientRegister;
